import React from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../../components/Header";
import { SDLSUrl } from "../../../../config";
import { executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";

const base_url = SDLSUrl.uri;

const policyTypeOptions = [
  { value: "NOTIFY", label: "Notify (SMS/Push Notification)" },
  { value: "LOCK", label: "Lock Device" },
  { value: "UNLOCK", label: "Unlock Device" },
  { value: "UNBIND", label: "Unbind Device" },
];

const validationSchema = yup.object().shape({
  policyType: yup.string().required("Policy type is required"),
  triggerDays: yup.number().required("Trigger days is required"),
  title: yup.string().max(20, "Title cannot exceed 20 characters").required("Title is required"),
  contentTemplate: yup.string().required("Content template is required"),
  contactNumber: yup.string().nullable(),
  emailAddress: yup.string().email("Enter a valid email").nullable(),
});

const DeviceLockPolicyForm = (props) => {
  const isEditing = !!props.data;

  const initialValues = {
    clientId: props.data?.ClientId ?? 1,
    productId: props.data?.ProductId ?? null,
    policyType: props.data?.PolicyType ?? "NOTIFY",
    triggerDays: props.data?.TriggerDays ?? 0,
    title: props.data?.Title ?? "",
    contentTemplate: props.data?.ContentTemplate ?? "Dear Customer, your loan payment is due on {dueDate}. Amount: KES {amountOwed}.",
    showType: props.data?.ShowType ?? 0,
    contactNumber: props.data?.ContactNumber ?? "",
    emailAddress: props.data?.EmailAddress ?? "",
    forbidIncomingCall: props.data?.ForbidIncomingCall ?? false,
    allowIncomingList: props.data?.AllowIncomingList ?? "",
    supportUserRefresh: props.data?.SupportUserRefresh ?? true,
    allowApp: props.data?.AllowApp ?? false,
    allowAppList: props.data?.AllowAppList ?? "",
    isActive: props.data?.IsActive ?? true,
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const mutationName = isEditing ? "updateDeviceLockPolicy" : "createDeviceLockPolicy";
      const payload = {
        ...(isEditing ? { id: props.data.Id } : {}),
        clientId: Number(values.clientId) || 1,
        productId: values.productId ? Number(values.productId) : null,
        policyType: values.policyType,
        triggerDays: Number(values.triggerDays),
        title: values.title,
        contentTemplate: values.contentTemplate,
        showType: Number(values.showType) || 0,
        contactNumber: values.contactNumber || null,
        emailAddress: values.emailAddress || null,
        forbidIncomingCall: !!values.forbidIncomingCall,
        allowIncomingList: values.allowIncomingList || null,
        supportUserRefresh: !!values.supportUserRefresh,
        allowApp: !!values.allowApp,
        allowAppList: values.allowAppList || null,
        isActive: !!values.isActive,
      };

      const response = await executeAnyMutation(
        mutationName,
        { policy: payload },
        "FEOutput",
        false,
        base_url
      );

      if (response?.results) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: `Policy successfully ${isEditing ? "updated" : "created"}!`,
          timer: 2000,
          showConfirmButton: false,
        });
        if (props.onSuccess) props.onSuccess();
        if (props.closeModal) props.closeModal();
      } else {
        const errMsg = response?.error ? JSON.parse(response.error)?.Error || response.error : "Failed to save policy.";
        Swal.fire("Error", errMsg, "error");
      }
    } catch (err) {
      console.error("Policy submit error:", err);
      Swal.fire("Error", err.message || "An unexpected error occurred.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "EDIT POLICY" : "CREATE POLICY"}
        subtitle={isEditing ? "Update Device Lock Trigger Policy" : "Create New Device Lock Trigger Policy"}
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: "span 4" },
              }}
            >
              <TextField
                select
                fullWidth
                variant="filled"
                label="Policy Type"
                name="policyType"
                value={values.policyType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.policyType && !!errors.policyType}
                helperText={touched.policyType && errors.policyType}
                sx={{ gridColumn: "span 2" }}
              >
                {policyTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Trigger Days (relative to due date: -1 = 1 day before, +1 = 1 day overdue)"
                name="triggerDays"
                value={values.triggerDays}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.triggerDays && !!errors.triggerDays}
                helperText={touched.triggerDays && errors.triggerDays}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Notification Title (Max 20 chars)"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Contact Support Phone Number"
                name="contactNumber"
                value={values.contactNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.contactNumber && !!errors.contactNumber}
                helperText={touched.contactNumber && errors.contactNumber}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                multiline
                rows={3}
                label="Content Template ({amountOwed}, {dueDate}, {accountNumber})"
                name="contentTemplate"
                value={values.contentTemplate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.contentTemplate && !!errors.contentTemplate}
                helperText={touched.contentTemplate && errors.contentTemplate}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Allowed Incoming Calls (Semicolon separated numbers)"
                name="allowIncomingList"
                value={values.allowIncomingList}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                label="Allowed Apps (Package names e.g. com.mpesa.android)"
                name="allowAppList"
                value={values.allowAppList}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <Box sx={{ gridColumn: "span 4", display: "flex", gap: 3, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.forbidIncomingCall}
                      onChange={(e) => setFieldValue("forbidIncomingCall", e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Forbid Incoming Calls"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.allowApp}
                      onChange={(e) => setFieldValue("allowApp", e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Allow Selected Apps in Lock Mode"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Policy Active"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap={2}>
              {props.closeModal && (
                <Button onClick={props.closeModal} color="neutral" variant="outlined">
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isEditing ? "Update Policy" : "Create Policy"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DeviceLockPolicyForm;
